import { Component } from '@theme/component';

/**
 * Mini review carousel shown beneath the buy buttons. Presents one review at a time in a
 * scroll-snap track and keeps the pagination dots in sync.
 *
 * @typedef {object} MiniReviewsRefs
 * @property {HTMLElement} [track] - The horizontally scrollable track holding the reviews
 * @property {HTMLElement[]} [dot] - The pagination dots
 *
 * @extends {Component<MiniReviewsRefs>}
 */
export class MiniReviewsComponent extends Component {
  /** @type {HTMLElement | null} */
  #boundTrack = null;

  #onScroll = () => this.#sync();

  connectedCallback() {
    super.connectedCallback();
    this.#bindTrack();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindTrack();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#boundTrack?.removeEventListener('scroll', this.#onScroll);
    this.#boundTrack = null;
  }

  /**
   * Scroll to a specific review by index.
   *
   * @param {{ index?: number }} [data] - Parsed declarative event data
   * @param {Event} [event] - The triggering event
   */
  goTo(data, event) {
    event?.preventDefault();
    this.#scrollToIndex(Number(data?.index ?? 0));
  }

  /**
   * Move one review forward or backward.
   *
   * @param {{ direction?: number }} [data] - Parsed declarative event data
   * @param {Event} [event] - The triggering event
   */
  step(data, event) {
    event?.preventDefault();
    this.#scrollToIndex(this.#activeIndex() + Number(data?.direction ?? 1));
  }

  #bindTrack() {
    const track = this.refs.track ?? null;
    if (this.#boundTrack === track) return;
    this.#boundTrack?.removeEventListener('scroll', this.#onScroll);
    this.#boundTrack = track;
    this.#boundTrack?.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #pageWidth() {
    const track = this.refs.track;
    return track ? track.clientWidth : 0;
  }

  #activeIndex() {
    const track = this.refs.track;
    const width = this.#pageWidth();
    if (!track || !width) return 0;
    return Math.round(track.scrollLeft / width);
  }

  #scrollToIndex(index) {
    const track = this.refs.track;
    if (!track) return;
    const count = track.children.length;
    const clamped = Math.max(0, Math.min(Number(index) || 0, count - 1));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: clamped * this.#pageWidth(), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #sync() {
    const dots = this.refs.dot;
    if (!dots?.length) return;
    const active = this.#activeIndex();
    dots.forEach((dot, index) => {
      const isActive = index === active;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('mini-reviews')) {
  customElements.define('mini-reviews', MiniReviewsComponent);
}
